import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, View, SlotInfo } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar.css';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  Assignment as TaskIcon,
  Group as TeamIcon,
  ConfirmationNumber as TicketIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import kalenderService, {
  Kalender,
  KalenderEreignis,
  CreateKalenderEreignisRequest,
  UpdateKalenderEreignisRequest,
  KalenderBerechtigung,
  CreateKalenderRequest,
} from '../services/kalenderService';
import { benutzerApi } from '../services/api';
import type { Benutzer } from '../types/api';

// Setup the localizer for date-fns with German locale
const locales = { de };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom messages in German
const messages = {
  allDay: 'Ganztägig',
  previous: 'Zurück',
  next: 'Weiter',
  today: 'Heute',
  month: 'Monat',
  week: 'Woche',
  day: 'Tag',
  agenda: 'Agenda',
  date: 'Datum',
  time: 'Zeit',
  event: 'Ereignis',
  noEventsInRange: 'Keine Ereignisse in diesem Zeitraum.',
  showMore: (total: number) => `+ ${total} weitere`,
};

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: KalenderEreignis;
}

const DRAWER_WIDTH = 280;

// Helper function to format date for datetime-local input
const formatDateTimeLocal = (date: Date): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

// Helper function to parse datetime-local input
const parseDateTimeLocal = (value: string): Date => {
  return new Date(value);
};

const KalenderPage: React.FC = () => {
  const [kalender, setKalender] = useState<Kalender[]>([]);
  const [ereignisse, setEreignisse] = useState<KalenderEreignis[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [view, setView] = useState<View>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [activeKalenderIds, setActiveKalenderIds] = useState<number[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(true);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEreignis, setSelectedEreignis] = useState<KalenderEreignis | null>(null);

  // New Calendar Dialog State
  const [newKalenderDialogOpen, setNewKalenderDialogOpen] = useState(false);
  const [newKalenderName, setNewKalenderName] = useState('');
  const [newKalenderTyp, setNewKalenderTyp] = useState('Team');
  const [newKalenderFarbe, setNewKalenderFarbe] = useState('#8B5CF6');
  const [newKalenderBeschreibung, setNewKalenderBeschreibung] = useState('');

  // Permissions Dialog State
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permKalender, setPermKalender] = useState<Kalender | null>(null);
  const [berechtigungen, setBerechtigungen] = useState<KalenderBerechtigung[]>([]);
  const [loadingPerm, setLoadingPerm] = useState(false);
  const [allBenutzer, setAllBenutzer] = useState<Benutzer[]>([]);
  const [selectedBenutzerId, setSelectedBenutzerId] = useState<number>(0);
  const [selectedBerechtigung, setSelectedBerechtigung] = useState<string>('Lesen');
  const [addingPerm, setAddingPerm] = useState(false);

  // Form State
  const [formKalenderId, setFormKalenderId] = useState<number>(0);
  const [formTitel, setFormTitel] = useState('');
  const [formBeschreibung, setFormBeschreibung] = useState('');
  const [formOrt, setFormOrt] = useState('');
  const [formStartDatum, setFormStartDatum] = useState<string>('');
  const [formEndDatum, setFormEndDatum] = useState<string>('');
  const [formGanztaegig, setFormGanztaegig] = useState(false);
  const [formPrioritaet, setFormPrioritaet] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const von = startOfMonth(currentDate);
      const bis = endOfMonth(addMonths(currentDate, 1));

      const data = await kalenderService.getUebersicht(
        format(von, 'yyyy-MM-dd'),
        format(bis, 'yyyy-MM-dd')
      );

      setKalender(data.kalender);
      setEreignisse(data.ereignisse);

      // Initialize active calendars
      if (activeKalenderIds.length === 0 && data.kalender.length > 0) {
        setActiveKalenderIds(data.kalender.map(k => k.kalenderID));
      }

      setError(null);
    } catch (err: any) {
      console.error('Error loading calendar data:', err);
      setError(err.response?.data?.error || 'Fehler beim Laden der Kalender-Daten');
    } finally {
      setLoading(false);
    }
  }, [currentDate, activeKalenderIds.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Convert events when data changes
  useEffect(() => {
    const filteredEreignisse = ereignisse.filter(e =>
      activeKalenderIds.includes(e.kalenderID)
    );

    const calendarEvents: CalendarEvent[] = filteredEreignisse.map(e => ({
      id: e.ereignisID,
      title: e.titel,
      start: new Date(e.startDatum),
      end: new Date(e.endDatum),
      allDay: e.ganztaegig,
      resource: e,
    }));

    setEvents(calendarEvents);
  }, [ereignisse, activeKalenderIds]);

  // Toggle calendar visibility
  const handleToggleKalender = (kalenderId: number) => {
    setActiveKalenderIds(prev =>
      prev.includes(kalenderId)
        ? prev.filter(id => id !== kalenderId)
        : [...prev, kalenderId]
    );
  };

  // Navigate calendar
  const handleNavigate = (newDate: Date) => {
    setCurrentDate(newDate);
  };

  // Open dialog for new event
  const handleSelectSlot = (slotInfo: SlotInfo) => {
    setEditMode(false);
    setSelectedEreignis(null);
    setFormTitel('');
    setFormBeschreibung('');
    setFormOrt('');
    setFormStartDatum(formatDateTimeLocal(slotInfo.start));
    setFormEndDatum(formatDateTimeLocal(slotInfo.end));
    setFormGanztaegig(false);
    setFormPrioritaet('');

    // Default to first active calendar
    const defaultKalender = kalender.find(k => activeKalenderIds.includes(k.kalenderID));
    setFormKalenderId(defaultKalender?.kalenderID || 0);

    setDialogOpen(true);
  };

  // Open dialog for existing event
  const handleSelectEvent = (event: CalendarEvent) => {
    const e = event.resource;
    setEditMode(true);
    setSelectedEreignis(e);
    setFormKalenderId(e.kalenderID);
    setFormTitel(e.titel);
    setFormBeschreibung(e.beschreibung || '');
    setFormOrt(e.ort || '');
    setFormStartDatum(formatDateTimeLocal(new Date(e.startDatum)));
    setFormEndDatum(formatDateTimeLocal(new Date(e.endDatum)));
    setFormGanztaegig(e.ganztaegig);
    setFormPrioritaet(e.prioritaet || '');
    setDialogOpen(true);
  };

  // Save event
  const handleSaveEreignis = async () => {
    if (!formTitel.trim() || !formStartDatum || !formEndDatum || !formKalenderId) {
      setError('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      const startDate = parseDateTimeLocal(formStartDatum);
      const endDate = parseDateTimeLocal(formEndDatum);

      if (editMode && selectedEreignis) {
        const data: UpdateKalenderEreignisRequest = {
          titel: formTitel,
          beschreibung: formBeschreibung || undefined,
          ort: formOrt || undefined,
          startDatum: startDate.toISOString(),
          endDatum: endDate.toISOString(),
          ganztaegig: formGanztaegig,
          prioritaet: formPrioritaet || undefined,
          kalenderID: formKalenderId,
        };
        await kalenderService.updateEreignis(selectedEreignis.ereignisID, data);
        setSuccess('Ereignis aktualisiert');
      } else {
        const data: CreateKalenderEreignisRequest = {
          kalenderID: formKalenderId,
          titel: formTitel,
          beschreibung: formBeschreibung || undefined,
          ort: formOrt || undefined,
          startDatum: startDate.toISOString(),
          endDatum: endDate.toISOString(),
          ganztaegig: formGanztaegig,
          prioritaet: formPrioritaet || undefined,
        };
        await kalenderService.createEreignis(data);
        setSuccess('Ereignis erstellt');
      }

      setDialogOpen(false);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  // Delete event
  const handleDeleteEreignis = async () => {
    if (!selectedEreignis) return;

    try {
      await kalenderService.deleteEreignis(selectedEreignis.ereignisID);
      setSuccess('Ereignis gelöscht');
      setDialogOpen(false);
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  // Create new calendar
  const handleCreateKalender = async () => {
    if (!newKalenderName.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    try {
      const request: CreateKalenderRequest = {
        name: newKalenderName,
        typ: newKalenderTyp,
        farbe: newKalenderFarbe,
        beschreibung: newKalenderBeschreibung || undefined,
      };
      await kalenderService.createKalender(request);
      setSuccess('Kalender erfolgreich erstellt');
      setNewKalenderDialogOpen(false);
      setNewKalenderName('');
      setNewKalenderBeschreibung('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Kalenders');
    }
  };

  // Open permissions dialog
  const handleOpenPermissions = async (k: Kalender) => {
    setPermKalender(k);
    setPermDialogOpen(true);
    setLoadingPerm(true);
    setSelectedBenutzerId(0);
    setSelectedBerechtigung('Lesen');
    try {
      const [perms, benutzerResponse] = await Promise.all([
        kalenderService.getBerechtigungen(k.kalenderID),
        benutzerApi.getMandantBenutzer()
      ]);
      setBerechtigungen(perms);
      setAllBenutzer(benutzerResponse.data);
    } catch (err: any) {
      setError('Fehler beim Laden der Berechtigungen');
    } finally {
      setLoadingPerm(false);
    }
  };

  // Add permission
  const handleAddBerechtigung = async () => {
    if (!permKalender || !selectedBenutzerId) {
      setError('Bitte waehlen Sie einen Benutzer aus');
      return;
    }
    setAddingPerm(true);
    try {
      const newPerm = await kalenderService.addBerechtigung(permKalender.kalenderID, {
        benutzerID: selectedBenutzerId,
        berechtigung: selectedBerechtigung,
      });
      setBerechtigungen(prev => [...prev, newPerm]);
      setSelectedBenutzerId(0);
      setSuccess('Berechtigung hinzugefuegt');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Hinzufuegen der Berechtigung');
    } finally {
      setAddingPerm(false);
    }
  };

  // Remove permission
  const handleRemoveBerechtigung = async (berechtigungId: number) => {
    try {
      await kalenderService.removeBerechtigung(berechtigungId);
      setBerechtigungen(prev => prev.filter(b => b.berechtigungID !== berechtigungId));
      setSuccess('Berechtigung entfernt');
    } catch (err: any) {
      setError('Fehler beim Entfernen der Berechtigung');
    }
  };

  // Get icon for calendar type
  const getKalenderIcon = (typ: string) => {
    switch (typ) {
      case 'Aufgaben': return <TaskIcon />;
      case 'Tickets': return <TicketIcon />;
      case 'Team': return <TeamIcon />;
      default: return <EventIcon />;
    }
  };

  // Event style based on calendar color
  const eventStyleGetter = (event: CalendarEvent) => {
    const e = event.resource;
    const color = e.farbe || e.kalenderFarbe || '#3B82F6';

    return {
      style: {
        backgroundColor: color,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  if (loading && kalender.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Sidebar Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: 'relative',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Meine Kalender</Typography>
          <Box>
            <Tooltip title="Neuer Kalender">
              <IconButton size="small" onClick={() => setNewKalenderDialogOpen(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}>
              <ChevronLeftIcon />
            </IconButton>
          </Box>
        </Box>
        <Divider />
        <List>
          {kalender.map(k => (
            <ListItem key={k.kalenderID} disablePadding>
              <ListItemButton
                onClick={() => handleToggleKalender(k.kalenderID)}
                dense
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={activeKalenderIds.includes(k.kalenderID)}
                    disableRipple
                    sx={{
                      color: k.farbe || '#3B82F6',
                      '&.Mui-checked': { color: k.farbe || '#3B82F6' },
                    }}
                  />
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 36, color: k.farbe || '#3B82F6' }}>
                  {getKalenderIcon(k.typ)}
                </ListItemIcon>
                <ListItemText
                  primary={k.name}
                  secondary={`${k.ereignisseCount} Ereignisse`}
                />
                {k.typ === 'Team' && (
                  <Tooltip title="Berechtigungen">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPermissions(k);
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {!drawerOpen && (
              <IconButton onClick={() => setDrawerOpen(true)}>
                <ChevronRightIcon />
              </IconButton>
            )}
            <Typography variant="h4" component="h1">
              Kalender
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Aktualisieren">
              <IconButton onClick={loadData} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Calendar */}
        <Paper sx={{ p: 2, height: 'calc(100vh - 200px)', minHeight: '600px' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            messages={messages}
            culture="de"
            view={view}
            onView={setView}
            date={currentDate}
            onNavigate={handleNavigate}
            eventPropGetter={eventStyleGetter}
          />
        </Paper>
      </Box>

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editMode ? 'Ereignis bearbeiten' : 'Neues Ereignis'}
            <IconButton size="small" onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Kalender *</InputLabel>
                <Select
                  value={formKalenderId}
                  label="Kalender *"
                  onChange={e => setFormKalenderId(Number(e.target.value))}
                >
                  {kalender.filter(k => activeKalenderIds.includes(k.kalenderID)).map(k => (
                    <MenuItem key={k.kalenderID} value={k.kalenderID}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: k.farbe || '#3B82F6',
                          }}
                        />
                        {k.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Titel *"
                fullWidth
                value={formTitel}
                onChange={e => setFormTitel(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Beschreibung"
                fullWidth
                multiline
                rows={3}
                value={formBeschreibung}
                onChange={e => setFormBeschreibung(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ort"
                fullWidth
                value={formOrt}
                onChange={e => setFormOrt(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formGanztaegig}
                    onChange={e => setFormGanztaegig(e.target.checked)}
                  />
                }
                label="Ganztägig"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start *"
                type="datetime-local"
                fullWidth
                value={formStartDatum}
                onChange={e => setFormStartDatum(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Ende *"
                type="datetime-local"
                fullWidth
                value={formEndDatum}
                onChange={e => setFormEndDatum(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Priorität</InputLabel>
                <Select
                  value={formPrioritaet}
                  label="Priorität"
                  onChange={e => setFormPrioritaet(e.target.value)}
                >
                  <MenuItem value="">Keine</MenuItem>
                  <MenuItem value="Niedrig">Niedrig</MenuItem>
                  <MenuItem value="Mittel">Mittel</MenuItem>
                  <MenuItem value="Hoch">Hoch</MenuItem>
                  <MenuItem value="Dringend">Dringend</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {selectedEreignis?.ticketTitel && (
              <Grid item xs={12}>
                <Chip
                  icon={<TicketIcon />}
                  label={`Ticket: ${selectedEreignis.ticketTitel}`}
                  color="primary"
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {editMode && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteEreignis}
            >
              Löschen
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSaveEreignis}
            startIcon={editMode ? <EditIcon /> : <AddIcon />}
          >
            {editMode ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Calendar Dialog */}
      <Dialog open={newKalenderDialogOpen} onClose={() => setNewKalenderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuer Kalender</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={newKalenderName}
              onChange={(e) => setNewKalenderName(e.target.value)}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Typ</InputLabel>
              <Select
                value={newKalenderTyp}
                label="Typ"
                onChange={(e) => setNewKalenderTyp(e.target.value)}
              >
                <MenuItem value="Team">Team-Kalender</MenuItem>
                <MenuItem value="Aufgaben">Aufgaben</MenuItem>
                <MenuItem value="Tickets">Tickets</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Farbe</InputLabel>
              <Select
                value={newKalenderFarbe}
                label="Farbe"
                onChange={(e) => setNewKalenderFarbe(e.target.value)}
              >
                <MenuItem value="#8B5CF6">Lila (Team)</MenuItem>
                <MenuItem value="#3B82F6">Blau</MenuItem>
                <MenuItem value="#10B981">Gruen</MenuItem>
                <MenuItem value="#EF4444">Rot</MenuItem>
                <MenuItem value="#F59E0B">Orange</MenuItem>
                <MenuItem value="#EC4899">Pink</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Beschreibung"
              value={newKalenderBeschreibung}
              onChange={(e) => setNewKalenderBeschreibung(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewKalenderDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleCreateKalender} variant="contained" color="primary">
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onClose={() => setPermDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Berechtigungen: {permKalender?.name}
        </DialogTitle>
        <DialogContent>
          {loadingPerm ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Add new permission section */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Benutzer hinzufuegen:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'flex-start' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Benutzer</InputLabel>
                  <Select
                    value={selectedBenutzerId}
                    label="Benutzer"
                    onChange={(e) => setSelectedBenutzerId(Number(e.target.value))}
                  >
                    <MenuItem value={0}>-- Benutzer waehlen --</MenuItem>
                    {allBenutzer
                      .filter(b => !berechtigungen.some(p => p.benutzerID === b.benutzerID))
                      .map(b => (
                        <MenuItem key={b.benutzerID} value={b.benutzerID}>
                          {b.vorname} {b.nachname}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Berechtigung</InputLabel>
                  <Select
                    value={selectedBerechtigung}
                    label="Berechtigung"
                    onChange={(e) => setSelectedBerechtigung(e.target.value)}
                  >
                    <MenuItem value="Lesen">Lesen</MenuItem>
                    <MenuItem value="Schreiben">Schreiben</MenuItem>
                    <MenuItem value="Verwalten">Verwalten</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddBerechtigung}
                  disabled={!selectedBenutzerId || addingPerm}
                  startIcon={addingPerm ? <CircularProgress size={16} /> : <PersonAddIcon />}
                >
                  Hinzufuegen
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Aktuelle Berechtigungen:
              </Typography>
              {berechtigungen.length === 0 ? (
                <Typography color="text.secondary">
                  Keine Berechtigungen vergeben. Nur der Ersteller hat Zugriff.
                </Typography>
              ) : (
                <List dense>
                  {berechtigungen.map((b) => (
                    <ListItem
                      key={b.berechtigungID}
                      secondaryAction={
                        <IconButton edge="end" size="small" onClick={() => handleRemoveBerechtigung(b.berechtigungID)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <PersonAddIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={b.benutzerName || b.gruppeName || 'Unbekannt'}
                        secondary={b.berechtigung}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)}>Schliessen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KalenderPage;
